import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/http';

export type AiState = 'READY' | 'DISABLED' | 'BUDGET_EXCEEDED';
export type Verdict = 'USEFUL' | 'NOT_USEFUL' | 'APPLIED' | 'IGNORED';
export type Confidence = 'ALTA' | 'MEDIA' | 'BAIXA';

/** Por que a função não atende agora (o backend devolve o primeiro que falta). */
export type AiUnavailableReason =
  | 'DISABLED'
  | 'BUDGET_EXCEEDED'
  | 'PLATFORM_BUDGET_EXCEEDED'
  | 'FUNCTION_OFF'
  | 'NO_PROMPT'
  | 'NO_MODEL'
  | 'NO_FINANCIAL_PROVIDER';

/**
 * O motivo dito para quem usa a empresa: o que é dela (teto) e o que é da equipe troig (provedor,
 * modelo, instrução), para saber a quem pedir.
 */
export const AI_UNAVAILABLE_TEXT: Record<AiUnavailableReason, string> = {
  DISABLED:
    'A IA não está ligada para esta empresa: o administrador do troig liga e define o teto do mês.',
  BUDGET_EXCEEDED:
    'A empresa atingiu o teto de IA deste mês. Volta no mês que vem ou com um teto maior.',
  PLATFORM_BUDGET_EXCEEDED:
    'O troig atingiu o limite de IA deste mês para todas as empresas. Tente de novo mais tarde.',
  FUNCTION_OFF: 'Esta função de IA está desligada pela equipe troig.',
  NO_PROMPT: 'Esta função de IA ainda não tem instrução publicada pela equipe troig.',
  NO_MODEL:
    'Nenhum modelo de IA com chave está escolhido para esta função. Fale com a equipe troig.',
  NO_FINANCIAL_PROVIDER:
    'Quem vê custo e margem só é atendido por um provedor de IA liberado para dados financeiros, e nenhum está. Fale com a equipe troig.',
};

export interface FunctionCoverage {
  available: boolean;
  reason: AiUnavailableReason | null;
  /** Prompt geral ativo: todo caso é atendido. Sem ele, só os de `scopes`. */
  general: boolean;
  scopes: string[];
}

export interface AiStatus {
  state: AiState;
  functions: Record<
    'finding-explanation' | 'executive-summary' | 'support-reply' | 'listing-advice' | 'assistant',
    FunctionCoverage
  >;
}

/** O caso (tipo de alerta) tem prompt que o atenda agora. */
export function covers(coverage: FunctionCoverage | undefined, scope: string): boolean {
  return Boolean(coverage?.available && (coverage.general || coverage.scopes.includes(scope)));
}

export interface Fact {
  id: string;
  label: string;
  value: string;
}

export interface FindingExplanation {
  summary: string;
  cause: string;
  actions: { title: string; detail: string; effort: 'BAIXO' | 'MEDIO' | 'ALTO' }[];
  evidence: string[];
  assumptions: string[];
  limitations: string[];
  confidence: Confidence;
}

export interface ExecutiveSummary {
  headline: string;
  overview: string;
  priorities: { findingId: string; reason: string }[];
  highlights: { text: string; evidence: string[] }[];
  limitations: string[];
  confidence: Confidence;
}

export interface Insight<TOutput> {
  id: string;
  kind: 'FINDING_EXPLANATION' | 'EXECUTIVE_SUMMARY';
  cached: boolean;
  model: string;
  promptVersion: string;
  createdAt: string;
  output: TOutput;
  facts: Fact[];
  findings?: { id: string; title: string }[];
  myFeedback: Verdict | null;
}

export const aiKeys = {
  all: ['ai'] as const,
  status: () => [...aiKeys.all, 'status'] as const,
};

export function useAiStatus() {
  return useQuery({ queryKey: aiKeys.status(), queryFn: () => apiRequest<AiStatus>('/ai') });
}

/** O consumo muda o teto: depois de cada chamada, o estado é relido. */
function useRefreshStatus() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: aiKeys.status() });
}

export function useExplainFinding() {
  const refresh = useRefreshStatus();
  return useMutation({
    mutationFn: ({ findingId, redo }: { findingId: string; redo?: boolean }) =>
      apiRequest<Insight<FindingExplanation>>(
        `/ai/findings/${findingId}/explanation${redo ? '?refazer=true' : ''}`,
        { method: 'POST' },
      ),
    onSettled: refresh,
  });
}

export function useExecutiveSummary() {
  const refresh = useRefreshStatus();
  return useMutation({
    mutationFn: (redo: boolean) =>
      apiRequest<Insight<ExecutiveSummary>>(`/ai/summary${redo ? '?refazer=true' : ''}`, {
        method: 'POST',
      }),
    onSettled: refresh,
  });
}

export function useAiFeedback() {
  return useMutation({
    mutationFn: ({ insightId, verdict }: { insightId: string; verdict: Verdict }) =>
      apiRequest<void>(`/ai/insights/${insightId}/feedback`, { method: 'PUT', body: { verdict } }),
  });
}
